import React, { useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useExam } from "../context/ExamContext";
import { useInstitute } from "../context/InstituteContext";
import { loginRequest, saveSession } from "../api/auth";
import { instituteLogin } from "../api/instituteclient";
import AuthLayout, { Field, PasswordInput } from "./Authlayout";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MOBILE_RE = /^\+?\d{10,15}$/;

function validateIdentifier(value) {
  const identifier = value.trim();

  if (!identifier) return "Enter your email or mobile number.";

  if (identifier.includes("@")) {
    if (!EMAIL_RE.test(identifier)) return "Enter a valid email address.";
    return undefined;
  }

  const mobile = identifier.replace(/[\s-]/g, "");
  if (!MOBILE_RE.test(mobile)) {
    return "Enter a valid email address or mobile number.";
  }
  return undefined;
}

function validatePassword(value) {
  if (!value) return "Enter your password.";
  if (!value.trim()) return "Password cannot be only spaces.";
  return undefined;
}

function validateLogin({ identifier, password }) {
  const errors = {};

  const idError = validateIdentifier(identifier);
  if (idError) errors.identifier = idError;

  const pwError = validatePassword(password);
  if (pwError) errors.password = pwError;

  return errors;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentUser } = useExam();
  const { setCurrentInstitute } = useInstitute();

  const [form, setForm] = useState({ identifier: "", password: "" });
  const [keepLoggedIn, setKeepLoggedIn] = useState(false); // UI only for now
  const [errors, setErrors] = useState({});
  const [loginError, setLoginError] = useState("");
  const [busy, setBusy] = useState(false);

  const identifierRef = useRef(null);
  const passwordRef = useRef(null);

  const handleChange = (name) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
    if (loginError) setLoginError("");
  };

  // Validate a field when the user leaves it (only if something was typed).
  const handleIdentifierBlur = () => {
    if (!form.identifier) return;
    setErrors((current) => ({
      ...current,
      identifier: validateIdentifier(form.identifier),
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");

    const found = validateLogin(form);
    setErrors(found);

    if (found.identifier) {
      identifierRef.current?.focus();
      return;
    }
    if (found.password) {
      passwordRef.current?.focus();
      return;
    }

    const email = form.identifier.trim();
    const password = form.password;

    setBusy(true);

    try {
      // 1) Institute account? (separate institute login)
      try {
        const institute = await instituteLogin(email, password);

        setCurrentUser(null);
        setCurrentInstitute(institute);

        if (institute.mustChangePassword) {
          navigate("/institute/change-password", { replace: true });
        } else {
          navigate("/institute/dashboard", { replace: true });
        }
        return;
      } catch (instituteError) {
        // The account may be a student/admin, the password may be wrong,
        // or the server may have failed. Fall through to the Django login.
      }

      // 2) Student / beginner / admin login through Django
      const response = await loginRequest(email, password);
      const data = response.data;

      if (!data?.access || !data?.refresh) {
        throw new Error("The server did not return valid login tokens.");
      }

      saveSession({
        access: data.access,
        refresh: data.refresh,
      });

      const user = data.user;

      if (!user) {
        throw new Error(
          "Login succeeded but no user information was returned."
        );
      }

      if (!user.role) {
        throw new Error("Django did not return the account role.");
      }

      window.dispatchEvent(new Event("auth:changed"));

      if (user.is_staff || user.is_superuser || user.role === "admin") {
        setCurrentInstitute(null);
        setCurrentUser(user);
        navigate("/admin/dashboard", { replace: true });
        return;
      }

      setCurrentInstitute(null);
      setCurrentUser(user);

      // Beginner accounts must use the separate beginner portal.
      if (user.role === "beginner" || user.account_type === "BEGINNER") {
        navigate("/beginner/dashboard", { replace: true });
        return;
      }

      // Preserve the existing Professional Student flow.
      const destination = location.state?.from || "/home";
      navigate(destination, { replace: true });
    } catch (error) {
      setLoginError(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          "Invalid email or password."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout active="login">
      <form onSubmit={handleLogin} noValidate>
        <h2>Welcome back</h2>
        <p className="sub">Log in to continue your exam preparation.</p>

        <Field
          id="li-id"
          label="Email or mobile number"
          error={errors.identifier}
        >
          <input
            id="li-id"
            ref={identifierRef}
            type="text"
            autoComplete="username"
            placeholder="Enter your email or mobile number"
            maxLength={254}
            value={form.identifier}
            onChange={handleChange("identifier")}
            onBlur={handleIdentifierBlur}
            aria-invalid={!!errors.identifier}
          />
        </Field>

        <Field id="li-pw" label="Password" error={errors.password}>
          <PasswordInput
            id="li-pw"
            inputRef={passwordRef}
            value={form.password}
            onChange={handleChange("password")}
            autoComplete="current-password"
            placeholder="Enter your password"
          />
        </Field>

        <div className="row">
          <label className="chk">
            <input
              type="checkbox"
              checked={keepLoggedIn}
              onChange={(e) => setKeepLoggedIn(e.target.checked)}
            />{" "}
            Keep me logged in
          </label>
          <Link to="/forgot-password">Forgot password?</Link>
        </div>

        <button className="btn" type="submit" disabled={busy}>
          {busy ? "Logging in..." : "Log in"}
        </button>

        {loginError && (
          <div className="msg error" role="alert">
            {loginError}
          </div>
        )}
      </form>
    </AuthLayout>
  );
}